/**
 * GxBandPicker (screens §P6 Beat 1 guided; content §2.4): the guided call, on the navy sheet CallTheYear draws. Four bands per KPI,
 * each with its range from `ForecastStatus.bands` (never typed here); scored on direction and band. Posts `{format: 'band', …}`.
 */
import type { ForecastBand } from "@bsim/domain";
import { formatKpi, lastYearLabel } from "../../format";
import type { BandPickerProps } from "../slotTypes";
import { BAND_KPIS, bandParts, impliedLevel } from "./guided";
import { usePersistedCall } from "../forecast/forecast";
import "./guided.css";
import { RovingGroup } from "../../theme/roving";

type Picks = Partial<Record<(typeof BAND_KPIS)[number]["kpi"], ForecastBand>>;

export function BandPicker({ status, pending, solo = false, onCommit }: BandPickerProps) {
  // Kept per cohort page and round (the round's last-year values differ), so a reload before the commit keeps the picks.
  const [picks, setPicks] = usePersistedCall<Picks>(`bsim.band.${window.location.pathname}.${JSON.stringify(status.noChangeBaseline)}`, {});
  const { nim, ncoRate, cet1Marked } = picks;
  const ready = nim !== undefined && ncoRate !== undefined && cet1Marked !== undefined;
  const missing = BAND_KPIS.filter((k) => picks[k.kpi] === undefined);
  if (status.bands === null) return <p className="gx-guided-note" data-testid="forecast.bandUnavailable">Band picker unavailable</p>;
  const bands = status.bands;
  return (
    <>
      <p className="gx-guided-help" data-testid="guided.bandPrompt">If your bank changes nothing this year (the no-change plan), where will each measure end? Pick a band, measured against last year. Your pick is checked against where that plan ends.</p>
      <div className="gx-guided-bands" data-testid="guided.bandPicker">
        {BAND_KPIS.map(({ kpi, label }) => (
          <RovingGroup key={kpi} className="gx-guided-band" role="radiogroup" aria-label={label}>
            <div className="gx-guided-band-nm">
              <span>{label}</span>
              <span className="gx-guided-band-last gx-num">{lastYearLabel(kpi)} {formatKpi(kpi, status.noChangeBaseline[kpi])}</span>
            </div>
            <div className="gx-guided-band-opts">
              {bands[kpi].map((range) => {
                const { name, range: rangeText } = bandParts(range.band, bands[kpi]);
                return (
                <button key={range.band} type="button" role="radio" aria-checked={picks[kpi] === range.band} className="gx-guided-band-o" data-testid={`guided.band.${kpi}.${range.band}`}
                  onClick={() => setPicks({ ...picks, [kpi]: range.band })}>
                  <b className="gx-guided-band-name">{name}</b>
                  <span className="gx-guided-band-range gx-num" data-testid={`guided.bandRange.${kpi}.${range.band}`}>{rangeText}</span>
                  <span className="gx-guided-band-level gx-num" data-testid={`guided.bandLevel.${kpi}.${range.band}`}>{impliedLevel(kpi, status.noChangeBaseline[kpi], range)}</span>
                </button>
                );
              })}
            </div>
            {kpi === "cet1Marked" ? <p className="gx-guided-band-note" data-testid="guided.cet1Note">Marked CET1 is capital after losses on the bonds the bank holds, so bond prices move it as well as earnings and payouts.</p> : null}
          </RovingGroup>
        ))}
      </div>
      <div className="gx-guided-foot">
        <span className="gx-guided-need">{missing.length === 0 ? "" : `${missing.map((k) => k.short).join(", ")} still needed`}</span>
        <button type="button" className="gx-btn gx-btn--accent" data-testid="forecast.commit" disabled={!ready || pending}
          onClick={() => { if (ready) onCommit({ format: "band", nim, ncoRate, cet1Marked }); }}>{pending ? "Committing\u2026" : solo ? "Commit my forecast" : "Commit our forecast"}</button>
      </div>
    </>
  );
}
